/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  ElectricalFittingSizeDto, FittingDto, ActionTypeEnumDto,
  CreateElectricalSizeInfoDto, CreateElectricalFittingSizeInfoDto, ActionPageEnumDto
} from 'api/models';
import { GenericMapper } from './GenericMapper';

export class ElectricalFittingSizeMapper implements GenericMapper {

  createAddSizeObject(
    dataArray: ElectricalFittingSizeDto[],
    userID: string,
    selectedFitting: FittingDto,
    statusId: ActionTypeEnumDto): any {
    const createSizeList: CreateElectricalSizeInfoDto[] = [];

    let maxID = 0;
    dataArray.forEach(element => {
      element.id = element.id > 0 ? element.id : maxID++;
      const newSize: CreateElectricalSizeInfoDto = {
        sizeID: element.sizeID,
        nominalSize: element.nominalSize,
        bdid: element.bdid,
        weightImperial: element.weightImperial,
        weightMetric: element.weightMetric,
        orderNumber: element.orderNumber !== undefined ? element.orderNumber : '0',
        startPage: element.startPage,
        endPage: element.endPage,
        id: element.id,
        hpid: element.hpid !== undefined ? element.hpid : '0',
        amtechID: element.amtechID > 0 ? element.amtechID : 0,
        sizeUnit: element.pricingUnit !== undefined ? element.pricingUnit : '0',
        discontinued: element.isDiscontinued !== null ? element.isDiscontinued : false
      };

      createSizeList.push(newSize);
    });

    const createfittingSizeInfo: CreateElectricalFittingSizeInfoDto = {
      fittingEntryID: selectedFitting.fittingEntryID,
      fittingIDNumber: selectedFitting.fittingIDNumber,
      comment: 'New added size',
      bdToolID: selectedFitting.bdToolID,
      changeby: userID,
      actionType: statusId,
      sizeList: createSizeList
    };
    return createfittingSizeInfo;
  }

  mapToFormGroup(source: ElectricalFittingSizeDto, unitId: number, page: ActionPageEnumDto): FormGroup {

    const formGroup = new FormGroup({
      id: new FormControl(source.id),
      isDiscontinued: new FormControl(source.isDiscontinued),
      isPartialySaved: new FormControl(source.isPartialySaved),
      nominalSize: new FormControl({ value: source.nominalSize, disabled: source.isPartialySaved || page === 'Update' },
        Validators.required),
      bdid: new FormControl({ value: source.bdid, disabled: true }, Validators.required),
      hpid: new FormControl({ value: source.hpid, disabled: unitId === 3 || source.isPartialySaved },
        [Validators.required]),
      amtechID: new FormControl({ value: source.amtechID, disabled: unitId === 1 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]*$')]),
      decSize1: new FormControl({ value: source.decSize1, disabled: true }, Validators.required),
      decSize2: new FormControl({ value: source.decSize2, disabled: true }, Validators.required),
      decSize3: new FormControl({ value: source.decSize3, disabled: true }, Validators.required),
      decSize4: new FormControl({ value: source.decSize4, disabled: true }, Validators.required),
      decSize5: new FormControl({ value: source.decSize5, disabled: true }, Validators.required),
      decSize6: new FormControl({ value: source.decSize6, disabled: true }, Validators.required),
      orderNumber: new FormControl({ value: source.orderNumber, disabled: source.isPartialySaved }, Validators.required),
      weightImperial: new FormControl({ value: source.weightImperial, disabled: unitId === 3 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]+(\.[0-9]?[0-9]?[0-9]?[0-9])?$')]),
      weightMetric: new FormControl({ value: source.weightMetric, disabled: unitId === 1 || source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]+(\.[0-9]?[0-9]?[0-9]?[0-9])?$')]),
      startPage: new FormControl({ value: source.startPage, disabled: source.isPartialySaved }, [Validators.required,
      Validators.pattern('^[0-9]*$')]),
      endPage: new FormControl({ value: source.endPage, disabled: source.isPartialySaved },
        [Validators.required, Validators.pattern('^[0-9]*$')]),
    });
    return formGroup;
  }

  mapToObject(controls): ElectricalFittingSizeDto {
    const nominalSizeDetails = controls[`nominalSize`].value;
    const newObject = {} as ElectricalFittingSizeDto;
    newObject.id = +controls[`id`].value;
    newObject.isDiscontinued = controls[`isDiscontinued`].value;
    newObject.amtechID = +controls[`amtechID`].value;
    newObject.sizeID = +nominalSizeDetails.sizeID;
    newObject.nominalSize = nominalSizeDetails.nominalSize;
    newObject.pricingUnit = nominalSizeDetails.pricingUnit;
    newObject.bdid = controls[`bdid`].value;
    newObject.hpid = controls[`hpid`].value;
    newObject.decSize1 = +controls[`decSize1`].value;
    newObject.decSize2 = +controls[`decSize2`].value;
    newObject.decSize3 = +controls[`decSize3`].value;
    newObject.decSize4 = +controls[`decSize4`].value;
    newObject.decSize5 = +controls[`decSize5`].value;
    newObject.decSize6 = +controls[`decSize6`].value;
    newObject.orderNumber = controls[`orderNumber`].value;
    newObject.weightImperial = +controls[`weightImperial`].value;
    newObject.weightMetric = +controls[`weightMetric`].value;
    newObject.startPage = +controls[`startPage`].value;
    newObject.endPage = +controls[`endPage`].value;

    return newObject;
  }


  getTableHeaders(unitId): any {
    let columnHeader;
    if (unitId === 1) {
      columnHeader = {
        nominalSize: 'Nominal Size',
        bdid: 'BDID',
        hpid: 'HPID',
        decSize1: 'Dec Size 1',
        decSize2: 'Dec Size 2',
        decSize3: 'Dec Size 3',
        decSize4: 'Dec Size 4',
        decSize5: 'Dec Size 5',
        decSize6: 'Dec Size 6',
        orderNumber: 'Order Number',
        weightImperial: 'Weight Imperial',
        weightMetric: 'Weight Metric',
        startPage: 'Start Page',
        endPage: 'End Page'
      };
    } else {
      columnHeader = {
        nominalSize: 'Nominal Size',
        bdid: 'BDID',
        amtechID: 'AmtechID',
        decSize1: 'Dec Size 1',
        decSize2: 'Dec Size 2',
        decSize3: 'Dec Size 3',
        decSize4: 'Dec Size 4',
        decSize5: 'Dec Size 5',
        decSize6: 'Dec Size 6',
        orderNumber: 'Order Number',
        weightImperial: 'Weight Imperial',
        weightMetric: 'Weight Metric',
        startPage: 'Start Page',
        endPage: 'End Page'
      };
    }
    return columnHeader;
  }

  setDefaultValues(elementForm: FormGroup, baseObject: ElectricalFittingSizeDto, selectedFitting: FittingDto) {
    elementForm.controls[`decSize1`].setValue(baseObject.decSize1);
    elementForm.controls[`decSize2`].setValue(baseObject.decSize2);
    elementForm.controls[`decSize3`].setValue(baseObject.decSize3);
    elementForm.controls[`decSize4`].setValue(baseObject.decSize4);
    elementForm.controls[`decSize5`].setValue(baseObject.decSize5);
    elementForm.controls[`decSize6`].setValue(baseObject.decSize6);
    elementForm.controls[`startPage`].setValue(baseObject.startPage);
    elementForm.controls[`endPage`].setValue(baseObject.endPage);
    elementForm.controls[`bdid`].setValue((String)(selectedFitting.fittingIDNumber + baseObject.sizeID));

    if (selectedFitting.unitID === 1) {

      elementForm.controls[`weightMetric`].setValue(elementForm.controls[`weightImperial`].value / 2.2046);

    }
    if (selectedFitting.unitID === 3) {
      elementForm.controls[`weightImperial`].setValue(elementForm.controls[`weightMetric`].value * 2.2046);
    }
  }

  setValuesByElement(elementForm: FormGroup, baseObject: ElectricalFittingSizeDto, nominalSizeList: any, isPartialySaved?: boolean) {
    const selectedNominalSize = nominalSizeList.find(x => x.nominalSize === baseObject.nominalSize);
    elementForm.controls[`id`].setValue(baseObject.id);
    elementForm.controls[`isDiscontinued`].setValue(baseObject.isDiscontinued);
    elementForm.controls[`isPartialySaved`].setValue(baseObject.isPartialySaved);
    elementForm.controls[`nominalSize`].setValue(selectedNominalSize);
    elementForm.controls[`bdid`].setValue(baseObject.bdid);
    elementForm.controls[`hpid`].setValue(baseObject.hpid);
    elementForm.controls[`amtechID`].setValue(baseObject.amtechID);
    elementForm.controls[`decSize1`].setValue(baseObject.decSize1);
    elementForm.controls[`decSize2`].setValue(baseObject.decSize2);
    elementForm.controls[`decSize3`].setValue(baseObject.decSize3);
    elementForm.controls[`decSize4`].setValue(baseObject.decSize4);
    elementForm.controls[`decSize5`].setValue(baseObject.decSize5);
    elementForm.controls[`decSize6`].setValue(baseObject.decSize6);
    elementForm.controls[`orderNumber`].setValue(baseObject.orderNumber);
    elementForm.controls[`weightImperial`].setValue(baseObject.weightImperial);
    elementForm.controls[`weightMetric`].setValue(baseObject.weightMetric);
    elementForm.controls[`startPage`].setValue(baseObject.startPage);
    elementForm.controls[`endPage`].setValue(baseObject.endPage);
  }
}
